import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import type {
  BankDetailsCheckInput,
  BankDetailsCheckResult,
  BulkSendResult,
  BulkSendVia,
  CreateReceiptInput,
  ReceiptDetailResponse,
  ReceiptSummary,
  RejectReceiptInput,
  UpdateReceiptInput,
} from '../../../types/api';

/**
 * Pull the server's `message` out of an axios error so the toast shows the
 * concrete reason ("Cannot edit a receipt in status 'approved'", etc.)
 * instead of a generic "failed to update". Falls back to the supplied
 * default when the response shape isn't recognised.
 */
function extractServerMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    if (typeof data?.message === 'string' && data.message.length > 0) {
      return data.message;
    }
  }
  return fallback;
}

export function useBulkSendReceipts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { receipt_ids: string[]; via?: BulkSendVia }) => {
      const res = await apiClient.post<BulkSendResult>(endpoints.receipts.bulkSend, input);
      return res.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
    },
  });
}

export function useSendAllUnsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { via?: BulkSendVia } = {}) => {
      const res = await apiClient.post<BulkSendResult>(endpoints.receipts.sendAllUnsent, input);
      return res.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
    },
  });
}

export function useCreateReceipt() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateReceiptInput) =>
      apiClient.post<ReceiptDetailResponse>(endpoints.receipts.create, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
    },
    onError: () => {
      toast.error('Failed to create receipt. Please try again.');
    },
  });
}

export function useUpdateReceipt(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateReceiptInput) =>
      apiClient.put<ReceiptDetailResponse>(endpoints.receipts.update(encId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
    },
    onError: (err) => {
      // Surface the server's specific reason — most importantly the 409
      // "Cannot edit a receipt in status '<x>'" so admins understand
      // approved/archived receipts are immutable rather than guessing
      // why their save failed.
      toast.error(extractServerMessage(err, 'Failed to update receipt. Please try again.'));
    },
  });
}

export function useApproveReceipt(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.post<ReceiptSummary>(endpoints.receipts.approve(encId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
      toast.success('Receipt approved');
    },
    onError: () => {
      toast.error('Failed to approve receipt.');
    },
  });
}

export function useRejectReceipt(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: RejectReceiptInput) =>
      apiClient.post<ReceiptSummary>(endpoints.receipts.reject(encId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
      toast.success('Receipt rejected');
    },
    onError: () => {
      toast.error('Failed to reject receipt.');
    },
  });
}

export function useDeleteReceipt() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    // A receipt Defra holds needs a reason; the server refuses the delete without one.
    mutationFn: (arg: string | { encId: string; reason?: string }) => {
      const encId = typeof arg === 'string' ? arg : arg.encId;
      const reason = typeof arg === 'string' ? undefined : arg.reason;
      return apiClient.delete(
        endpoints.receipts.delete(encId),
        reason === undefined ? undefined : { data: { reason } },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
      toast.success('Receipt deleted');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;
      toast.error(msg ?? 'Failed to delete receipt.');
    },
  });
}

export function useArchiveReceipt() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (encId: string) =>
      apiClient.post<ReceiptDetailResponse>(endpoints.receipts.archive(encId)),
    onSuccess: (_data, encId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
      toast.success('Receipt archived');
    },
    onError: () => {
      toast.error('Failed to archive receipt.');
    },
  });
}

export interface SendReceiptInput {
  encId: string;
  via?: 'email' | 'sms';
  /** One-off override — when present, dispatch to this address instead of
   *  the customer snapshot on the receipt. Server treats it as a per-send
   *  override (no persistence). */
  toEmail?: string;
  /** One-off override — when present, dispatch to this number instead of
   *  the customer snapshot on the receipt. */
  toPhone?: string;
}

export function useSendReceipt() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ encId, via = 'email', toEmail, toPhone }: SendReceiptInput) => {
      const body: { via: 'email' | 'sms'; to_email?: string; to_phone?: string } = { via };
      if (toEmail !== undefined && toEmail.length > 0) body.to_email = toEmail;
      if (toPhone !== undefined && toPhone.length > 0) body.to_phone = toPhone;
      return apiClient.post<ReceiptDetailResponse>(endpoints.receipts.send(encId), body);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(vars.encId) });
      toast.success('Receipt sent.');
    },
    // Errors are surfaced by the per-call onError handler in
    // SendReceiptModal so the user sees the server's specific reason
    // (e.g. missing template / blank customer email) inline. No generic
    // toast here — it would clobber the more useful inline message.
  });
}

export interface UploadReceiptPhotosInput {
  encId: string;
  files: File[];
  /** Optional canonical slot name per file (same order), stored as the caption. */
  captions?: string[];
}

// The receipt id is passed at mutate time, not at hook-creation time. The
// create pages only learn the id from the create response, and binding it via
// a hook argument captured the pre-create empty string — firing the upload at
// POST /receipts//photos (a 404). Taking it per-call keeps the id and the
// upload in the same closure.
export function useUploadReceiptPhotos() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ encId, files, captions }: UploadReceiptPhotosInput) => {
      if (!encId) {
        throw new Error('Cannot upload photos: receipt id is missing.');
      }
      const fd = new FormData();
      for (const f of files) fd.append('photos', f);
      for (const c of captions ?? []) fd.append('captions', c);
      const res = await apiClient.raw.post(endpoints.receipts.photos(encId), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (_data, { encId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.photos(encId) });
    },
    onError: () => {
      toast.error('Failed to upload photos.');
    },
  });
}

export function useDeleteReceiptPhoto(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (photoEncId: string) =>
      apiClient.delete(endpoints.receipts.photo(encId, photoEncId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.photos(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.receipts.detail(encId) });
    },
    onError: () => {
      toast.error('Failed to delete photo.');
    },
  });
}

/**
 * Batch 7b: ask the server whether saving these bank details would overwrite
 * the linked customer's stored ones (field names only — values are admin-only).
 */
export function useCheckBankDetails() {
  return useMutation({
    mutationFn: async (input: BankDetailsCheckInput) => {
      const res = await apiClient.post<BankDetailsCheckResult>(
        endpoints.receipts.bankDetailsCheck,
        input,
      );
      return res.data;
    },
  });
}
